import { Component } from '@theme/component';

class XomdMediaCarousel extends Component {
  /** @type {Map<HTMLVideoElement, {button: HTMLButtonElement, mode: string, muted: boolean, loop: boolean, request?: symbol}>} */
  videos = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.cleanup();
    this.initialize();
  }

  disconnectedCallback() {
    this.cleanup();
    super.disconnectedCallback();
  }

  initialize() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.track = this.querySelector('[data-carousel-track]');
    this.previousButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.previousButton?.addEventListener('click', () => this.move(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.move(1), { signal });
    this.track?.addEventListener('scroll', () => this.updateControls(), { passive: true, signal });
    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    if (this.track) this.resizeObserver.observe(this.track);
    this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)');
    this.hoverQuery.addEventListener('change', () => {
      if (!this.hoverQuery.matches) {
        for (const [video, state] of this.videos) {
          if (state.mode === 'hover') this.resetVideo(video);
        }
      }
    }, { signal });
    for (const button of this.querySelectorAll('[data-video-play]')) {
      const media = button.closest('.xomd-video-card__media');
      const video = media?.querySelector('video');
      if (!(button instanceof HTMLButtonElement) || !(video instanceof HTMLVideoElement)) continue;
      this.videos.set(video, { button, mode: 'idle', muted: video.muted, loop: video.loop });
      button.addEventListener('click', () => this.playVideo(video, 'manual'), { signal });
      media.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse' && this.hoverQuery.matches) this.previewVideo(video);
      }, { signal });
      media.addEventListener('pointerleave', () => {
        if (this.videos.get(video)?.mode === 'hover') this.resetVideo(video);
      }, { signal });
      video.addEventListener('playing', () => {
        const state = this.videos.get(video);
        if (state) state.button.hidden = state.mode === 'manual';
      }, { signal });
      video.addEventListener('pause', () => {
        const state = this.videos.get(video);
        if (state) state.button.hidden = false;
      }, { signal });
      video.addEventListener('ended', () => this.resetVideo(video), { signal });
      video.addEventListener('error', () => this.resetVideo(video), { signal });
    }
    this.frame = requestAnimationFrame(() => this.updateControls());
  }

  cleanup() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    for (const video of this.videos.keys()) this.resetVideo(video);
    this.videos.clear();
  }

  updateControls() {
    if (!this.track) return;
    const maxScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
    if (this.previousButton) this.previousButton.disabled = this.track.scrollLeft <= 2;
    if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 2;
    // A swipe or scroll must also stop a preview that has left the visible track.
    const trackBounds = this.track.getBoundingClientRect();
    for (const [video, state] of this.videos) {
      if (state.mode !== 'hover') continue;
      const bounds = video.getBoundingClientRect();
      if (bounds.right <= trackBounds.left || bounds.left >= trackBounds.right) this.resetVideo(video);
    }
  }

  move(direction) {
    const card = this.track?.querySelector('[data-carousel-card]');
    if (!card) return;
    for (const video of this.videos.keys()) this.resetVideo(video);
    const gap = Number.parseFloat(getComputedStyle(this.track).columnGap || 0);
    this.track.scrollBy({
      left: direction * (card.getBoundingClientRect().width + gap),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  }

  /** Stop playback and restore the card's original media state. */
  resetVideo(video) {
    const state = this.videos.get(video);
    if (!state) return;
    const wasPreview = state.mode === 'hover';
    state.mode = 'idle';
    state.request = undefined;
    video.pause();
    video.controls = false;
    video.muted = state.muted;
    video.loop = state.loop;
    video.removeAttribute('tabindex');
    state.button.hidden = false;
    if (document.activeElement === video) state.button.focus({ preventScroll: true });
    if (wasPreview && video.readyState > 0) video.currentTime = 0;
  }

  /** Hover previews never interrupt a video the customer is watching. */
  previewVideo(video) {
    const state = this.videos.get(video);
    if (!state || state.mode !== 'idle') return;
    for (const [other, otherState] of this.videos) {
      if (otherState.mode === 'manual' && !other.paused) return;
    }
    this.playVideo(video, 'hover');
  }

  /** @param {HTMLVideoElement} video @param {'manual' | 'hover'} mode */
  async playVideo(video, mode) {
    const state = this.videos.get(video);
    if (!state) return;
    for (const other of this.videos.keys()) {
      if (other !== video) this.resetVideo(other);
    }
    if (state.mode === 'hover' && mode === 'manual' && video.readyState > 0) video.currentTime = 0;
    state.mode = mode;
    const request = Symbol('play');
    state.request = request;
    video.muted = mode === 'hover' ? true : state.muted;
    video.loop = mode === 'hover' ? true : state.loop;
    video.controls = mode === 'manual';
    if (mode === 'manual') {
      video.tabIndex = 0;
      // Keep keyboard focus on the usable native controls when the overlay hides.
      if (document.activeElement === state.button) video.focus({ preventScroll: true });
    }
    try {
      await video.play();
      if (state.request === request) state.button.hidden = mode === 'manual';
    } catch {
      // A cancelled preview must not undo a newer click or play request.
      if (state.request === request) this.resetVideo(video);
    }
  }
}

class XomdTestimonialSlideshow extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.slides = [...this.querySelectorAll('[data-testimonial-slide]')];
    this.counter = this.querySelector('[data-testimonial-current]');
    this.index = 0;
    this.querySelector('[data-testimonial-prev]')?.addEventListener('click', () => this.show(this.index - 1));
    this.querySelector('[data-testimonial-next]')?.addEventListener('click', () => this.show(this.index + 1));
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());
    this.show(0);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  show(index) {
    if (!this.slides.length) return;
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, slideIndex) => {
      slide.hidden = slideIndex !== this.index;
      slide.setAttribute('aria-hidden', slideIndex === this.index ? 'false' : 'true');
    });
    if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.dataset.autoplay !== 'true' || this.slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const interval = Math.max(Number.parseInt(this.dataset.interval || '6000', 10), 4000);
    this.autoplayTimer = window.setInterval(() => this.show(this.index + 1), interval);
  }

  stopAutoplay() {
    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = undefined;
  }
}

if (!customElements.get('xomd-media-carousel')) customElements.define('xomd-media-carousel', XomdMediaCarousel);
if (!customElements.get('xomd-testimonial-slideshow')) customElements.define('xomd-testimonial-slideshow', XomdTestimonialSlideshow);
